import { getCloneObject, isDefined, isMacintosh, isSameId } from "../helpers";
import { stringToDate } from "../common/date";
import { userColors, priorityColors } from "./constants";
export function getMenuOptions({ store, type, id, source }) {
    switch (type) {
        case "task":
            return getTaskOptions(store, source);
        case "toolbar":
            return getToolbarOptions(store, id);
        case "user":
            return true;
    }
}
function getAvailableUsers(store, id, extra) {
    const users = getCloneObject(store.getState().users);
    const task = store.getTask(id);
    const data = [];
    if (users.length) {
        for (let index = 0; index < users.length; index++) {
            const user = users[index];
            const checked = task?.assigned?.some(i => isSameId(i, user.id));
            data.push({
                ...user,
                id: extra + ":" + user.id,
                color: user.color || userColors[index % userColors.length],
                icon: checked ? "check" : "empty",
                type: "user",
                clickable: true,
                checked,
            });
        }
    }
    return data;
}
function getAvailableProjects(store, extra) {
    const activeProject = store.getState().activeProject;
    let projects = getCloneObject(store.getState().projects);
    if (projects.every(p => isDefined(p.id))) {
        projects = [{ id: null, label: "No project" }, ...projects];
    }
    const data = [];
    if (projects.length) {
        for (let index = 0; index < projects.length; index++) {
            const project = projects[index];
            const checked = isSameId(project.id, activeProject);
            data.push({
                ...project,
                id: extra + ":" + project.id,
                type: "item",
                icon: checked ? "check" : "empty",
                checked,
            });
        }
    }
    return data;
}
function getPriorityItems(store, id, priorities) {
    const task = store.getTask(id);
    const priorityItems = [];
    for (let index = 0; index < priorities.length; index++) {
        const item = priorities[index];
        priorityItems.push({
            ...item,
            id: "priority:" + item.id,
            type: "priority",
            hotkey: item.hotkey,
            color: item.color || priorityColors[index % priorityColors.length],
            icon: isSameId(task?.priority, item.id) ? "check" : "empty",
        });
    }
    return [
        ...priorityItems,
        { type: "separator" },
        {
            type: "item",
            icon: "refresh",
            label: "Reset priority",
            hotkey: "Alt+0",
            id: "priority:reset",
        },
    ];
}
function getTaskOptions(store, source = []) {
    const { filter, projects, users, taskShape, priorities } = store.getState();
    const firstSelected = source.at(0);
    const dateFormat = taskShape?.date?.format;
    const options = [];
    let dueDate = store.getTask(firstSelected)?.due_date;
    if (typeof dueDate === "string") {
        dueDate = stringToDate(dueDate, dateFormat);
    }
    if (!isDefined(filter) && source.length === 1) {
        options.push({
            type: "item",
            icon: "plus",
            label: "Add task below",
            hotkey: "Enter",
            id: "addBelow",
        }, {
            type: "item",
            icon: "subtask",
            label: "Add subtask",
            id: "addSubtask",
        }, { type: "separator" });
    }
    options.push({
        type: "item",
        icon: "indent",
        label: "Indent",
        hotkey: "Tab",
        id: "indent",
    }, {
        type: "item",
        icon: "unindent",
        label: "Unindent",
        hotkey: "Shift+Tab",
        id: "unindent",
    }, {
        type: "item",
        icon: "calendar",
        label: "Set due date",
        id: "setDate",
        data: [
            {
                type: "datepicker",
                id: "dueDate",
                value: source.length === 1 && dueDate ? dueDate : null,
                store,
            },
        ],
    });
    if (Array.isArray(priorities) && priorities.length) {
        options.push({
            type: "item",
            icon: "alert",
            label: "Set priority",
            data: getPriorityItems(store, firstSelected, priorities),
            id: "setPriority",
        });
    }
    if (source.length === 1 && users?.length) {
        options.push({
            type: "item",
            icon: "assign",
            label: "Assign to",
            data: getAvailableUsers(store, firstSelected, "assign"),
            id: "assign",
        });
    }
    if (projects?.length) {
        options.push({
            type: "item",
            icon: "content-paste",
            label: "Move to",
            data: getAvailableProjects(store, "moveProject"),
            id: "moveProject",
        });
    }
    return [
        ...options,
        {
            type: "item",
            icon: "duplicate",
            label: "Duplicate",
            hotkey: isMacintosh() ? "CMD+D" : "Ctrl+D",
            id: "duplicate",
        },
        {
            type: "item",
            icon: "content-copy",
            label: "Copy",
            hotkey: isMacintosh() ? "CMD+C" : "Ctrl+C",
            id: "copy",
        },
        {
            type: "item",
            icon: "paste",
            label: "Paste",
            hotkey: isMacintosh() ? "CMD+V" : "Ctrl+V",
            id: "paste",
        },
        { type: "separator" },
        {
            type: "item",
            icon: "delete",
            label: "Delete",
            hotkey: "Delete",
            id: "delete",
        },
    ];
}
function getToolbarOptions(store, id) {
    const { taskShape, readonly } = store.getState();
    const isHide = taskShape?.completed?.taskHide || false;
    const options = [
        {
            id: "sort",
            type: "item",
            label: "Sort by",
            icon: "sort",
            data: [
                {
                    id: "sort:text-asc",
                    type: "item",
                    label: "Text (a-z)",
                    icon: "asc",
                },
                {
                    id: "sort:text-desc",
                    type: "item",
                    label: "Text (z-a)",
                    icon: "desc",
                },
                { type: "separator" },
                {
                    id: "sort:priority-asc",
                    type: "item",
                    label: "Priority (high to low)",
                    icon: "asc",
                },
                {
                    id: "sort:priority-desc",
                    type: "item",
                    label: "Priority (low to high)",
                    icon: "desc",
                },
                { type: "separator" },
                {
                    id: "sort:due-date-desc",
                    type: "item",
                    label: "Due date (new to old)",
                    icon: "asc",
                },
                {
                    id: "sort:due-date-asc",
                    type: "item",
                    label: "Due date (old to new)",
                    icon: "desc",
                },
                { type: "separator" },
                {
                    id: "sort:completion-date-desc",
                    type: "item",
                    label: "Completion date (new to old)",
                    icon: "asc",
                },
                {
                    id: "sort:completion-date-asc",
                    type: "item",
                    label: "Completion date (old to new)",
                    icon: "desc",
                },
                { type: "separator" },
                {
                    id: "sort:created-date-desc",
                    type: "item",
                    label: "Created (new to old)",
                    icon: "asc",
                },
                {
                    id: "sort:created-date-asc",
                    type: "item",
                    label: "Created (old to new)",
                    icon: "desc",
                },
                { type: "separator" },
                {
                    id: "sort:edited-date-desc",
                    type: "item",
                    label: "Edited (new to old)",
                    icon: "asc",
                },
                {
                    id: "sort:edited-date-asc",
                    type: "item",
                    label: "Edited (old to new)",
                    icon: "desc",
                },
            ],
        },
        {
            id: "completed",
            icon: "check",
            label: "Completed tasks",
            type: "item",
            data: [
                {
                    id: "completed:show",
                    label: "Show",
                    type: "item",
                    icon: !isHide ? "check" : "empty",
                },
                {
                    id: "completed:hide",
                    label: "Hide",
                    type: "item",
                    icon: isHide ? "check" : "empty",
                },
            ],
        },
    ];
    if (!readonly) {
        options.push({ type: "separator" }, { id: "add", icon: "plus", label: "Add project", type: "item" });
        if (isDefined(id)) {
            options.push({
                id: "rename",
                icon: "edit",
                label: "Rename project",
                type: "item",
            }, {
                id: "delete",
                icon: "delete",
                label: "Delete project",
                type: "item",
            });
        }
    }
    return options;
}
//# sourceMappingURL=menu.js.map